import { Outlet, createFileRoute } from '@tanstack/react-router';
import { Suspense, lazy } from 'react';
import { z } from 'zod';

import { retryImport } from '@/shared/lib/lazy-with-retry';
import { useMobile } from '@/shared/lib/use-mobile';
import { Skeleton } from '@/shared/ui/skeleton';

// A phone never downloads the desktop project page, and vice versa.
const ProjectMobilePage = lazy(() =>
  retryImport(() => import('@/pages/project/project-mobile-page')).then((m) => ({ default: m.ProjectMobilePage })),
);
const ProjectPage = lazy(() =>
  retryImport(() => import('@/pages/project/project-page')).then((m) => ({ default: m.ProjectPage })),
);

function ProjectSkeleton() {
  return (
    <div className="flex flex-col h-full p-4 gap-3" aria-busy="true">
      <Skeleton variant="card" className="h-40" />
      <Skeleton variant="card" className="h-10" />
      <Skeleton variant="card" className="h-32" />
    </div>
  );
}

// Search-param contract for the project page:
//   `?view=board`     — which tab/view is active; sharable via URL.
//   `?month=YYYY-MM`  — deep-links the Calendar tab to a specific month.
//   `?page=<id>`      — with `view=docs`, the phone opens that doc page.
// Both are optional; the page falls back to localStorage (per-project
// last-viewed) and finally to "overview" when neither is present.
const PROJECT_VIEWS = [
  'overview',
  'board',
  'list',
  'timeline',
  'calendar',
  'docs',
  'files',
  'milestones',
  'reports',
  'activity',
  'ai',
] as const;
const projectSearch = z.object({
  view: z.enum(PROJECT_VIEWS).optional(),
  month: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  // `?view=docs&page=<id>` — phone: open that page in the Docs tab (a page
  // just made from the Create sheet). Desktop ignores it.
  page: z.string().uuid().optional(),
});

// The canonical task deep link `/teams/$/projects/$/$taskKey` is a child of
// this route.  Desktop keeps the project underneath and opens the task modal
// over it (the child renders `TaskModal`); the mobile page swaps itself for
// the full-screen task card (it renders the `<Outlet />` on its own).
function ProjectRoute() {
  const isMobile = useMobile();
  if (isMobile) {
    return (
      <Suspense fallback={<ProjectSkeleton />}>
        <ProjectMobilePage />
      </Suspense>
    );
  }
  return (
    <>
      <Suspense fallback={<ProjectSkeleton />}>
        <ProjectPage />
      </Suspense>
      <Outlet />
    </>
  );
}

export const Route = createFileRoute('/teams/$teamSlug/projects/$projectSlug')({
  validateSearch: projectSearch,
  component: ProjectRoute,
});
